/**
 * Obras — triagem e acompanhamento.
 *
 * A decisão que este ecrã serve é uma só, e é cara nos dois sentidos: esta
 * obra pode ser orçamentada com o que o cliente enviou, ou obriga a mandar
 * um técnico ao local?
 *
 * Errar para um lado faz a Dozy pagar deslocações que ninguém pediu. Errar
 * para o outro põe prestadores a orçamentar 2.450 m² por três fotografias,
 * e o cliente recebe números que não significam nada.
 *
 * Por isso o painel mostra primeiro o que o cliente escreveu, e só depois os
 * dois botões — e o preço da avaliação é escrito aqui, à vista, porque é o
 * cliente que o vai pagar.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Hammer, MapPin, X, FileText, Send, BadgeCheck } from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import AnexoProjecto from '../../components/AnexoProjecto';
import FaturacaoObra from '../../components/FaturacaoObra';
import PerfilFiscalObra from '../../components/PerfilFiscalObra';

const CORES = {
  EM_TRIAGEM: 'bg-amber-500/15 text-amber-400',
  ORCAMENTO_REMOTO: 'bg-amber-500/15 text-amber-400',
  VISITA_NECESSARIA: 'bg-amber-500/15 text-amber-400',
  AGUARDA_PAGAMENTO_VISITA: 'bg-amber-500/15 text-amber-400',
  VISITA_POR_AGENDAR: 'bg-orange-500/15 text-orange-400',
  VISITA_AGENDADA: 'bg-blue-500/15 text-blue-400',
  LEVANTAMENTO_CONCLUIDO: 'bg-cyan-500/15 text-cyan-400',
  CADERNO_PRONTO: 'bg-cyan-500/15 text-cyan-400',
  EM_CONCURSO: 'bg-blue-500/15 text-blue-400',
  PROPOSTAS_RECEBIDAS: 'bg-green-500/15 text-green-400',
  PROPOSTA_ESCOLHIDA: 'bg-green-500/15 text-green-400',
  RASCUNHO: 'bg-slate-700 text-slate-300',
};

const kz = (v) => `${Number(v || 0).toLocaleString('pt-PT')} Kz`;

// O levantamento guarda os sim/não como 'true'/'false'. Sem isto, o caderno
// que vai para os concorrentes dizia "Tem ligação à terra: false".
const legivel = (v) => (v === true || v === 'true' ? 'Sim' : v === false || v === 'false' ? 'Não' : v);
const medicoes = (r) => Object.entries(r).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${legivel(v)}`);
const dt = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '—');

const ProjectsTriage = () => {
  const { success, error } = useToast();
  const [dados, setDados] = useState(null);
  const [aberto, setAberto] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [versaoFiscal, setVersaoFiscal] = useState(0);
  const [ambito, setAmbito] = useState(null);     // { valor, rotulo, motivo }
  const [zona, setZona] = useState(null);         // { valor, rotulo, motivo }
  const [zonas, setZonas] = useState([]);
  const [refReforco, setRefReforco] = useState('');
  const [refAdiantamento, setRefAdiantamento] = useState('');
  const [cobertura, setCobertura] = useState(null);
  const [preco, setPreco] = useState('');
  const [motivo, setMotivo] = useState('');
  const [caderno, setCaderno] = useState({ resumo: '', prazo_dias: '', valor_estimado_kz: '' });
  // As rubricas são o que torna as propostas comparáveis linha a linha. O
  // servidor já as aceitava e já havia tabela para as mostrar ao cliente; era
  // este formulário que nunca as pedia, e por isso as propostas só se podiam
  // comparar por totais — exactamente o que o ecrã do cliente diz não querer.
  const [rubricas, setRubricas] = useState([]);
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(() => {
    api.get('/projects')
      .then((r) => setDados(r.data))
      .catch(() => { setDados({ projectos: [], rotulos: {} }); error('Erro ao carregar as obras'); });
  }, [error]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/operations/zones').then((r) => setZonas(r.data || [])).catch(() => setZonas([]));
  }, []);

  const confirmarFase = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${aberto.project_id}/contrato/fase/confirmar`, {});
      setCobertura(data);
      success(`Fase confirmada. O valor protegido cobre ate a etapa ${data.coberto_ate_ordem}.`);
      abrir(aberto);
    } catch (e) { error(e.response?.data?.message || 'Nao foi possivel confirmar a fase.'); }
    finally { setBusy(false); }
  };

  const libertarAdiantamento = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${aberto.project_id}/contrato/adiantamento/libertar`,
        { referencia: refAdiantamento.trim() });
      success(`Adiantamento de ${kz(data.adiantamento_kz)} libertado.`);
      setRefAdiantamento('');
      abrir(aberto);
    } catch (e) { error(e.response?.data?.message || 'Nao foi possivel libertar o adiantamento.'); }
    finally { setBusy(false); }
  };

  const gravarZona = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/projects/${aberto.project_id}/zona`,
        { zone_id: zona.valor, motivo: zona.motivo.trim() });
      success(data.inalterado ? 'Ja estava assim.'
        : `Zona corrigida para ${data.zona || 'sem zona'}.`);
      setDetalhe((d) => ({ ...d, projecto: { ...d.projecto, zone_id: zona.valor } }));
      setZona(null);
    } catch (e) { error(e.response?.data?.message || 'Nao foi possivel corrigir a zona.'); }
    finally { setBusy(false); }
  };

  const gravarAmbito = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/projects/${aberto.project_id}/ambito`,
        { ambito: ambito.valor, motivo: ambito.motivo.trim() });
      success(data.inalterado ? 'Já estava assim.'
        : `Âmbito corrigido. O avaliador passa a levar ${data.checklist_perguntas} perguntas.`);
      setDetalhe((d) => ({ ...d, projecto: { ...d.projecto, ambito: ambito.valor } }));
      setAmbito(null);
    } catch (e) { error(e.response?.data?.message || 'Não foi possível corrigir.'); }
    finally { setBusy(false); }
  };

  const abrir = async (p) => {
    setAberto(p); setDetalhe(null); setPreco(''); setMotivo(''); setAConfirmarPagamento(false);
    setVisita({ surveyor_id: '', agendado_para: '' }); setRubricas([]);
    setAConfirmarObra(false); setEtapas(null); setRefPag('');
    if (['EM_EXECUCAO', 'AGUARDA_CONFIRMACAO', 'CONCLUIDO'].includes(p.estado)) carregarEtapas(p.project_id);
    setCaderno({ resumo: '', prazo_dias: '', valor_estimado_kz: '' });
    setFila(null);
    if (['EM_CONCURSO', 'PROPOSTAS_RECEBIDAS'].includes(p.estado)) carregarFila(p.project_id);
    try {
      const { data } = await api.get(`/projects/${p.project_id}`);
      setDetalhe(data);
      // Pré-preencher o caderno com o que o levantamento apurou poupa a
      // reescrita, mas fica editável: o relatório mede, não redige.
      const linhas = data.avaliacao?.relatorio ? medicoes(data.avaliacao.relatorio) : [];
      // As observações do técnico entram no caderno. É a parte que a checklist
      // não cobre — acessos, condicionantes, riscos — e ficava só no relatório,
      // fora do documento que os concorrentes recebem e orçamentam.
      if (data.avaliacao?.observacoes) {
        linhas.push('', 'Notas da vistoria:', data.avaliacao.observacoes.trim());
      }
      if (linhas.length) setCaderno((c) => ({ ...c, resumo: linhas.join('\n') }));

      // Já há caderno: rever parte do que está publicado, não de uma folha em
      // branco. Sem isto, corrigir uma linha obrigava a reescrever o resto — e
      // as rubricas perdiam-se na versão nova.
      if (data.caderno) {
        setCaderno({
          resumo: data.caderno.resumo || '',
          prazo_dias: data.caderno.prazo_dias || '',
          valor_estimado_kz: data.caderno.valor_estimado_kz || '',
        });
        setRubricas((data.caderno.itens || []).map((i) => ({
          descricao: i.descricao || '',
          quantidade: i.quantidade || '',
          unidade: i.unidade || '',
        })));
      }
    } catch { error('Não foi possível abrir a obra'); }
  };

  const triar = async (decisao) => {
    setBusy(true);
    try {
      await api.post(`/projects/${aberto.project_id}/triage`, {
        decisao, motivo: motivo || undefined,
        preco_kz: decisao === 'visita' && preco ? Number(preco) : undefined,
      });
      success(decisao === 'visita' ? 'Avaliação criada — o cliente vai receber o preço.' : 'Aberto a propostas.');
      setAberto(null); carregar();
    } catch (e) { error(e.response?.data?.message || 'Não foi possível decidir.'); }
    finally { setBusy(false); }
  };

  // Quem viu o dinheiro entrar é que confirma. O cliente só pede a
  // referência — marcar como pago nunca esteve do lado dele por acidente.
  // A pergunta é feita na própria página e não com window.confirm: as caixas
  // do navegador são bloqueadas em parte dos contextos onde a equipa trabalha,
  // e o botão ficava a não fazer nada sem dizer porquê.
  const [aConfirmarPagamento, setAConfirmarPagamento] = useState(false);

  // ── Designar o avaliador ──
  //
  // A rota existia desde o início e o painel nunca a expôs: a visita ficava
  // paga e sem técnico, sem que ninguém tivesse por onde a marcar.
  const [avaliadores, setAvaliadores] = useState([]);
  const [visita, setVisita] = useState({ surveyor_id: '', agendado_para: '' });

  useEffect(() => {
    api.get('/operations/avaliadores').then((r) => setAvaliadores(r.data || [])).catch(() => {});
  }, []);

  const designar = async () => {
    setBusy(true);
    try {
      // O campo datetime-local dá uma hora sem fuso ("2026-09-22T10:00"). Se
      // for enviada assim, a base guarda-a como UTC e o cliente lê-a mais uma
      // hora à frente — uma visita marcada para as 10 chegava-lhe como 11.
      await api.post(`/projects/${aberto.project_id}/survey/assign`, {
        ...visita, agendado_para: new Date(visita.agendado_para).toISOString(),
      });
      success('Visita marcada. O técnico e o cliente foram avisados.');
      setAberto(null); carregar();
    } catch (e) { error(e.response?.data?.message || 'Não foi possível marcar a visita.'); }
    finally { setBusy(false); }
  };

  // O dinheiro da obra, que e coisa diferente do dinheiro da visita: aqui
  // confirma-se a entrada do valor do contrato, que fica retido ate as etapas
  // serem aprovadas. Sem esta confirmacao a obra nao arranca.
  const [aConfirmarObra, setAConfirmarObra] = useState(false);

  // As etapas com dinheiro pronto a libertar. O cliente aprovou, o valor ficou
  // apontado -- e faltava quem o mandasse sair. Sem este passo, 'A_LIBERTAR'
  // era um estado terminal disfarcado.
  const [etapas, setEtapas] = useState(null);
  const [refPag, setRefPag] = useState('');

  const carregarEtapas = async (projectId) => {
    try {
      const { data } = await api.get(`/projects/${projectId}/etapas`);
      setEtapas(data?.etapas || []);
      api.get(`/projects/${projectId}/contrato/cobertura`)
        .then((r) => setCobertura(r.data)).catch(() => setCobertura(null));
    } catch { setEtapas([]); }
  };

  const libertarEtapa = async (etapa) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${aberto.project_id}/etapas/${etapa.etapa_id}/libertar`,
        { referencia: refPag.trim() || undefined });
      success(`${Number(data.liquido).toLocaleString('pt-PT')} Kz libertados. O executante foi avisado.`);
      setRefPag('');
      carregarEtapas(aberto.project_id);
    } catch (e) { error(e.response?.data?.message || 'Nao foi possivel libertar.'); }
    finally { setBusy(false); }
  };

  const confirmarPagamentoObra = async () => {
    setAConfirmarObra(false);
    setBusy(true);
    try {
      await api.post(`/projects/${aberto.project_id}/contrato/pagamento/confirmar`, {});
      success('Pagamento confirmado. O cliente ja ve a empresa e as duas partes podem assinar.');
      setAberto(null); carregar();
    } catch (e) { error(e.response?.data?.message || 'Nao foi possivel confirmar.'); }
    finally { setBusy(false); }
  };

  const confirmarReforco = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${aberto.project_id}/contrato/reforco/confirmar`,
        { referencia: refReforco.trim() });
      success(`${kz(data.liquidado_kz)} liquidados. As etapas voltam a poder ser libertadas.`);
      setRefReforco('');
      abrir(aberto);
    } catch (e) { error(e.response?.data?.message || 'Nao foi possivel confirmar.'); }
    finally { setBusy(false); }
  };

  const confirmarPagamento = async () => {
    setAConfirmarPagamento(false);
    setBusy(true);
    try {
      await api.post(`/projects/${aberto.project_id}/survey/confirm-payment`);
      success('Pagamento confirmado. A visita pode ser agendada.');
      setAberto(null); carregar();
    } catch (e) { error(e.response?.data?.message || 'Não foi possível confirmar.'); }
    finally { setBusy(false); }
  };

  // ── Fila de convites ──
  //
  // As rotas existiam e o painel nao as expunha. Consequencia pratica: uma
  // obra cujo concurso abrisse sem ninguem elegivel — porque ainda nao havia
  // prestadores verificados naquela categoria — ficava assim para sempre. A
  // fila nao se repoe sozinha quando aparece oferta nova; alguem tem de a
  // mandar repor, e nao havia por onde.
  const [fila, setFila] = useState(null);

  const carregarFila = async (projectId) => {
    try {
      const { data } = await api.get(`/projects/${projectId}/concurso`);
      setFila(data);
    } catch { setFila(null); }
  };

  const reporConvites = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${aberto.project_id}/concurso/repor`, {});
      success(data?.convidados
        ? `${data.convidados} convite(s) enviados.`
        : 'Nao ha mais prestadores elegiveis para convidar.');
      await carregarFila(aberto.project_id);
    } catch (e) { error(e.response?.data?.message || 'Nao foi possivel repor.'); }
    finally { setBusy(false); }
  };

  const novaRonda = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${aberto.project_id}/concurso/nova-ronda`, {});
      success('Nova ronda aberta.');
      await carregarFila(aberto.project_id);
    } catch (e) { error(e.response?.data?.message || 'Nao foi possivel abrir a ronda.'); }
    finally { setBusy(false); }
  };

  const publicarCaderno = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${aberto.project_id}/scope`, {
        resumo: caderno.resumo,
        prazo_dias: caderno.prazo_dias ? Number(caderno.prazo_dias) : undefined,
        valor_estimado_kz: caderno.valor_estimado_kz ? Number(caderno.valor_estimado_kz) : undefined,
        itens: rubricas
          .filter((r) => r.descricao.trim())
          .map((r, i) => ({
            descricao: r.descricao.trim(),
            quantidade: r.quantidade ? Number(r.quantidade) : undefined,
            unidade: r.unidade.trim() || undefined,
            ordem: i,
          })),
      });
      success('Caderno de encargos publicado. A obra está aberta a propostas.');
      setAberto(null); carregar();
    } catch (e) { error(e.response?.data?.message || 'Não foi possível publicar.'); }
    finally { setBusy(false); }
  };

  const lista = dados?.projectos || [];
  const rotulos = dados?.rotulos || {};
  // O que espera decisão vem primeiro — é para isso que se abre este ecrã.
  // O que espera decisão vem primeiro: triagem por fazer, e levantamento
  // entregue à espera de caderno.
  const ordem = { EM_TRIAGEM: 0, LEVANTAMENTO_CONCLUIDO: 1, ORCAMENTO_REMOTO: 2 };
  const ordenada = [...lista].sort((a, b) => (ordem[a.estado] ?? 9) - (ordem[b.estado] ?? 9));

  const emConcurso = ['EM_CONCURSO', 'PROPOSTAS_RECEBIDAS'].includes(aberto?.estado);

  const podeTriar = aberto?.estado === 'EM_TRIAGEM';
  // Um caderno com um erro ficava assim para sempre: o formulário desaparecia
  // no momento em que o concurso abria. Rever é publicar uma versão nova — os
  // concorrentes que já responderam são avisados para reverem a proposta.
  const publicaPrimeira = ['LEVANTAMENTO_CONCLUIDO', 'ORCAMENTO_REMOTO'].includes(aberto?.estado);
  const revisao = ['EM_CONCURSO', 'PROPOSTAS_RECEBIDAS'].includes(aberto?.estado);
  const podePublicar = publicaPrimeira || revisao;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Hammer className="w-6 h-6 text-blue-400" /> Obras &amp; Projetos
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Decida se a obra pode ser orçamentada à distância ou se exige visita técnica.
        </p>
      </div>

      {dados === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : ordenada.length === 0 ? (
        <p className="text-slate-500 py-10 text-center">Ainda não há obras submetidas.</p>
      ) : (
        <ul className="space-y-2">
          {ordenada.map((p) => (
            <li key={p.project_id}>
              <button onClick={() => abrir(p)}
                className="w-full text-left p-4 rounded-xl border border-slate-800 bg-slate-900/50 hover:bg-slate-900">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold text-slate-100">{p.titulo || p.categoria}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <span className="font-mono">{p.codigo}</span>
                      {p.cliente && <> · {p.cliente}</>}
                      {p.localizacao && <> · <MapPin className="w-3 h-3 inline" /> {p.localizacao}</>}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-md text-[11px] font-semibold ${CORES[p.estado] || 'bg-slate-700 text-slate-300'}`}>
                    {rotulos[p.estado] || p.estado}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {aberto && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => !busy && setAberto(null)}>
          <div onClick={(e) => e.stopPropagation()}
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl p-5 my-8 space-y-4">

            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="font-mono text-xs text-slate-500">{aberto.codigo}</div>
                <h2 className="text-lg font-bold text-white">{aberto.titulo || aberto.categoria}</h2>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[11px] font-semibold ${CORES[aberto.estado] || 'bg-slate-700 text-slate-300'}`}>
                  {rotulos[aberto.estado] || aberto.estado}
                </span>
              </div>
              <button onClick={() => setAberto(null)} aria-label="Fechar"
                className="grid place-items-center w-11 h-11 rounded-xl bg-slate-800 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!detalhe ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
            ) : (
              <>
                {/* O que o cliente escreveu — é sobre isto que se decide. */}
                <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
                  <p className="text-sm text-slate-300 whitespace-pre-wrap">{detalhe.projecto.descricao}</p>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-slate-500">
                    <div>Imóvel: <span className="text-slate-300">{detalhe.projecto.tipo_imovel || '—'}</span></div>
                    <div>Prazo: <span className="text-slate-300">{detalhe.projecto.prazo_desejado ? `${detalhe.projecto.prazo_desejado} dias` : '—'}</span></div>
                    <div>Início: <span className="text-slate-300">{dt(detalhe.projecto.inicio_desejado)}</span></div>
                    <div>Anexos: <span className="text-slate-300">{detalhe.documentos.length}</span></div>
                  </div>

                  {/* O âmbito decide que perguntas o avaliador leva para o
                      local. O cliente responde no formulário, mas nem sempre
                      sabe — e as obras anteriores a este campo ficaram todas
                      sem ele. Quem lê a descrição aqui percebe logo qual é. */}
                  <div className="mt-3 pt-3 border-t border-slate-800">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
                      Âmbito — decide a lista de verificação da vistoria
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {[['NOVA', 'Instalação nova'], ['EXISTENTE', 'Intervir no existente'],
                        [null, 'Não definido']].map(([v, r]) => (
                        <button key={r}
                          onClick={() => setAmbito({ valor: v, rotulo: r, motivo: '' })}
                          disabled={(detalhe.projecto.ambito || null) === v}
                          className={`min-h-[36px] px-3 rounded-lg text-xs ${
                            (detalhe.projecto.ambito || null) === v
                              ? 'bg-cyan-600 text-white font-semibold'
                              : 'bg-slate-800 text-slate-300'}`}>
                          {r}
                        </button>
                      ))}
                    </div>
                    {!detalhe.projecto.ambito && (
                      <p className="text-xs text-amber-500 mt-1.5">
                        Sem âmbito, o avaliador leva a lista de verificação genérica da categoria.
                      </p>
                    )}
                  </div>

                  {/* A zona era adivinhada da morada e nao se podia corrigir.
                      Desde que a morada so sai ate a zona para quem orca, um
                      engano aqui faz o preco vir errado - e decide tambem a
                      quem se oferece a visita. */}
                  <div className="mt-3 pt-3 border-t border-slate-800">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
                      Zona — e o que o prestador ve enquanto a obra nao esta paga
                    </div>
                    <select
                      value={detalhe.projecto.zone_id ?? ''}
                      onChange={(e) => setZona({
                        valor: e.target.value === '' ? null : Number(e.target.value),
                        rotulo: e.target.options[e.target.selectedIndex].text,
                        motivo: '',
                      })}
                      className="w-full min-h-[40px] px-3 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 text-sm">
                      <option value="">Sem zona</option>
                      {zonas.map((z) => (
                        <option key={z.zone_id} value={z.zone_id}>
                          {z.nome}{z.municipio ? ` — ${z.municipio}` : ''}
                        </option>
                      ))}
                    </select>
                    {!detalhe.projecto.zone_id && (
                      <p className="text-xs text-amber-500 mt-1.5">
                        Sem zona, quem orça lê apenas «Luanda» — e faz o preço às cegas.
                      </p>
                    )}
                    {detalhe.projecto.localizacao && (
                      <p className="text-xs text-slate-500 mt-1.5">
                        Morada registada: {detalhe.projecto.localizacao}
                      </p>
                    )}
                  </div>

                  {/* Sem ver as fotografias, a decisão "chega para orçamentar
                      à distância?" é um palpite. */}
                  {detalhe.documentos.length > 0 ? (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {detalhe.documentos.map((doc) => (
                        <AnexoProjecto key={doc.document_id} projectId={detalhe.projecto.project_id} anexo={doc} />
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-amber-500">
                      O cliente não juntou fotografias — quase de certeza que isto exige visita.
                    </p>
                  )}
                </div>

                {detalhe.avaliacao && (
                  <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-sm">
                    <div className="text-slate-400 text-xs uppercase tracking-wide font-semibold mb-1">Avaliação</div>
                    <div className="text-slate-300">
                      {kz(detalhe.avaliacao.preco_kz)} · {detalhe.avaliacao.estado}
                      {detalhe.avaliacao.pago_em && <> · paga a {dt(detalhe.avaliacao.pago_em)}</>}
                    </div>
                    {/* Sem gateway, é aqui que o pagamento entra no sistema. */}
                    {!detalhe.avaliacao.pago_em && !aConfirmarPagamento && (
                      <button onClick={() => setAConfirmarPagamento(true)} disabled={busy}
                        className="w-full mt-3 min-h-[44px] rounded-lg bg-green-600 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        <BadgeCheck className="w-4 h-4" /> Confirmar pagamento recebido
                      </button>
                    )}
                    {!detalhe.avaliacao.pago_em && aConfirmarPagamento && (
                      <div className="mt-3 p-3 rounded-lg bg-slate-900 border border-green-700">
                        <p className="text-sm text-slate-300">
                          Confirma que entraram {kz(detalhe.avaliacao.preco_kz)} com a referência
                          {' '}<span className="font-semibold text-slate-100">{detalhe.codigo || aberto.codigo || 'DZY'}-AV{String(detalhe.avaliacao.survey_id).padStart(3, '0')}</span>?
                          Depois disto o preço da visita já não se corrige.
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2 mt-3">
                          <button onClick={confirmarPagamento} disabled={busy}
                            className="min-h-[44px] rounded-lg bg-green-600 text-white font-semibold disabled:opacity-50">
                            Sim, o pagamento entrou
                          </button>
                          <button onClick={() => setAConfirmarPagamento(false)} disabled={busy}
                            className="min-h-[44px] rounded-lg bg-slate-800 text-slate-300 font-semibold border border-slate-700">
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Paga e sem técnico: é aqui que a visita deixa de ser
                        uma intenção e passa a ter dia, hora e responsável. */}
                    {detalhe.avaliacao.pago_em && !detalhe.avaliacao.concluido_em && (
                      <div className="mt-3 pt-3 border-t border-slate-800 space-y-2">
                        <div className="text-slate-400 text-xs uppercase tracking-wide font-semibold">
                          {detalhe.avaliacao.surveyor_id ? 'Remarcar a visita' : 'Marcar a visita'}
                        </div>
                        <select value={visita.surveyor_id}
                          onChange={(e) => setVisita((v) => ({ ...v, surveyor_id: e.target.value }))}
                          className="w-full min-h-[44px] px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200">
                          <option value="">Escolher o avaliador…</option>
                          {avaliadores.map((a) => (
                            <option key={a.admin_id} value={a.admin_id}>
                              {a.full_name}
                              {a.zonas?.length ? ` · ${a.zonas.map((z) => z.nome).join(', ')}` : ' · sem zonas'}
                              {` · ${a.visitas_abertas} por fazer`}
                            </option>
                          ))}
                        </select>
                        <input type="datetime-local" value={visita.agendado_para}
                          onChange={(e) => setVisita((v) => ({ ...v, agendado_para: e.target.value }))}
                          className="w-full min-h-[44px] px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200" />
                        <button onClick={designar}
                          disabled={busy || !visita.surveyor_id || !visita.agendado_para}
                          className="w-full min-h-[44px] rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">
                          {detalhe.avaliacao.surveyor_id ? 'Remarcar e avisar' : 'Marcar visita e avisar'}
                        </button>
                        {!avaliadores.length && (
                          <p className="text-xs text-amber-500">
                            Não há nenhuma conta de avaliador técnico activa.
                          </p>
                        )}
                      </div>
                    )}
                    {detalhe.avaliacao.surveyor_id && (
                      <div className="mt-2 text-xs text-slate-400">
                        Técnico designado
                        {detalhe.avaliacao.agendado_para
                          && ` · ${new Date(detalhe.avaliacao.agendado_para).toLocaleString('pt-PT')}`}
                      </div>
                    )}
                    {detalhe.avaliacao.relatorio && (
                      <pre className="mt-2 text-xs text-slate-400 whitespace-pre-wrap font-sans">
                        {medicoes(detalhe.avaliacao.relatorio).join('\n')}
                        {detalhe.avaliacao.observacoes ? `\n\n${detalhe.avaliacao.observacoes}` : ''}
                      </pre>
                    )}
                  </div>
                )}

                {/* ── Decisão de triagem ── */}
                {podeTriar && (
                  <div className="space-y-3">
                    <label htmlFor="motivo" className="block text-sm text-slate-400">Motivo (fica no histórico)</label>
                    <input id="motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}
                      placeholder="Ex.: área grande, fotografias insuficientes"
                      className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 outline-none focus:border-blue-500" />

                    <div className="grid gap-3 sm:grid-cols-2">
                      <button onClick={() => triar('remoto')} disabled={busy}
                        className="p-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-left disabled:opacity-50">
                        <div className="font-semibold text-slate-100">Orçamento remoto possível</div>
                        <div className="text-xs text-slate-400 mt-1">Abre já a propostas, sem deslocação.</div>
                      </button>

                      <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
                        <div className="font-semibold text-amber-200">Visita técnica necessária</div>
                        <label htmlFor="preco" className="block text-xs text-slate-400 mt-2 mb-1">
                          Preço da avaliação — é o que o cliente vai pagar
                        </label>
                        <input id="preco" type="number" min="1" value={preco} onChange={(e) => setPreco(e.target.value)}
                          placeholder="250000"
                          className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 outline-none focus:border-amber-500" />
                        <button onClick={() => triar('visita')} disabled={busy || !preco}
                          className="w-full mt-2 min-h-[44px] rounded-lg bg-amber-500 text-slate-950 font-semibold disabled:opacity-40">
                          Exigir visita
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── O dinheiro da obra ── */}
                {detalhe.contrato && (
                  <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-sm">
                    <div className="text-slate-400 text-xs uppercase tracking-wide font-semibold mb-1">
                      Contrato {detalhe.contrato.numero}
                    </div>
                    <div className="text-slate-300">
                      {kz(detalhe.contrato.valor_actual)} · {detalhe.contrato.estado}
                      {detalhe.contrato.escrow_pago_em
                        ? ` · pago a ${dt(detalhe.contrato.escrow_pago_em)}`
                        : ` · ${detalhe.contrato.escrow_estado === 'PAGAMENTO_PENDENTE'
                          ? 'a espera da transferencia' : 'por pagar'}`}
                    </div>

                    {/* Exigia contrato ACTIVO, ou seja assinado pelos dois.
                        Era a ordem antiga. Agora paga-se antes de assinar, por
                        isso o contrato esta sempre em AGUARDA_ASSINATURAS
                        quando o dinheiro entra: a condicao antiga nunca se
                        cumpria e o botao nunca aparecia a ninguem. Basta haver
                        contrato e o escrow por pagar. */}
                    {detalhe.contrato.estado !== 'CANCELADO' && !detalhe.contrato.escrow_pago_em && !aConfirmarObra && (
                      <button onClick={() => setAConfirmarObra(true)} disabled={busy}
                        className="w-full mt-3 min-h-[44px] rounded-lg bg-green-600 text-white font-semibold disabled:opacity-50">
                        Confirmar pagamento da obra
                      </button>
                    )}
                    {aConfirmarObra && (
                      <div className="mt-3 p-3 rounded-lg bg-slate-900 border border-green-700">
                        <p className="text-sm text-slate-300">
                          Confirma que entraram {kz(detalhe.contrato.escrow_kz || detalhe.contrato.valor_actual)}
                          {detalhe.contrato.escrow_referencia
                            ? <> com a referencia <span className="font-semibold text-slate-100">{detalhe.contrato.escrow_referencia}</span></>
                            : null}? O cliente passa a ver quem e a empresa, e as
                          duas partes podem assinar. A obra so arranca depois das
                          duas assinaturas.
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2 mt-3">
                          <button onClick={confirmarPagamentoObra} disabled={busy}
                            className="min-h-[44px] rounded-lg bg-green-600 text-white font-semibold disabled:opacity-50">
                            Sim, o pagamento entrou
                          </button>
                          <button onClick={() => setAConfirmarObra(false)} disabled={busy}
                            className="min-h-[44px] rounded-lg bg-slate-800 text-slate-300 font-semibold border border-slate-700">
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Pagamento faseado: a cobertura e o botao de confirmar a
                        fase ficam ao lado do escrow, porque e a mesma pergunta
                        — quanto dinheiro do cliente ja ca esta. */}
                    {cobertura?.faseado && (
                      <div className="mt-3 pt-3 border-t border-slate-800">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
                          Pagamento faseado · cobre ate a etapa {cobertura.coberto_ate_ordem || 0}
                        </div>
                        <div className="text-sm text-slate-300">
                          {kz(cobertura.pago_kz)} de {kz(cobertura.total_kz)} protegidos
                        </div>
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {(cobertura.etapas || []).map((e) => (
                            <span key={e.ordem} title={`${e.ordem}. ${e.titulo} — ${kz(e.quota_kz)}`}
                              className={`w-7 h-7 rounded-lg grid place-items-center text-xs font-bold ${
                                e.coberta ? 'bg-green-600/20 text-green-400' : 'bg-slate-800 text-slate-500'}`}>
                              {e.ordem}
                            </span>
                          ))}
                        </div>
                        {cobertura.proxima_fase ? (
                          <div className="mt-2">
                            <p className="text-xs text-slate-400 mb-2">
                              Fase por entrar: <strong className="text-slate-200">
                                {kz(cobertura.proxima_fase.valor_kz)}</strong>, para cobrir ate a
                              etapa {cobertura.proxima_fase.ate_ordem}
                              {cobertura.proxima_fase.referencia
                                && <> · ref <span className="font-mono">{cobertura.proxima_fase.referencia}</span></>}
                            </p>
                            <button onClick={confirmarFase} disabled={busy}
                              className="min-h-[44px] px-4 rounded-lg bg-green-600 text-white
                                         font-semibold text-sm disabled:opacity-50">
                              Confirmar entrada de {kz(cobertura.proxima_fase.valor_kz)}
                            </button>
                          </div>
                        ) : (
                          <p className="text-xs text-green-400 mt-2">A obra esta paga por inteiro.</p>
                        )}
                      </div>
                    )}

                    {/* Adiantamento de arranque. E o unico dinheiro que sai do
                        escrow contra trabalho nenhum, por isso fica aqui ao
                        lado do escrow e nao escondido noutro ecra: quem
                        liberta tem de ver, no mesmo sitio, que o dinheiro do
                        cliente ja entrou. */}
                    {Number(detalhe.contrato.adiantamento_kz || 0) > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-800">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
                          Adiantamento de arranque · {Number(detalhe.contrato.adiantamento_pct)}%
                        </div>
                        {detalhe.contrato.adiantamento_pago_em ? (
                          <div className="text-sm text-slate-300">
                            {kz(detalhe.contrato.adiantamento_kz)} libertado a{' '}
                            {dt(detalhe.contrato.adiantamento_pago_em)}
                            <span className="text-slate-500"> · ref {detalhe.contrato.adiantamento_referencia}</span>
                            <div className="text-xs text-slate-400 mt-1">
                              Reposto até agora: {kz(detalhe.contrato.adiantamento_amortizado_kz || 0)} de{' '}
                              {kz(detalhe.contrato.adiantamento_kz)}
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-xs text-slate-400 mb-2">
                              {kz(detalhe.contrato.adiantamento_kz)} a transferir à empresa antes de
                              haver trabalho feito. Volta a entrar a{' '}
                              {Number(detalhe.contrato.adiantamento_pct)}% em cada etapa aprovada.
                            </p>
                            <div className="flex gap-2 flex-wrap">
                              <input value={refAdiantamento}
                                onChange={(e) => setRefAdiantamento(e.target.value)}
                                placeholder="Referência da transferência"
                                className="flex-1 min-w-[180px] min-h-[44px] px-3 rounded-lg bg-slate-950
                                           border border-slate-700 text-slate-100 text-sm" />
                              <button onClick={libertarAdiantamento}
                                disabled={busy || !refAdiantamento.trim()}
                                className="min-h-[44px] px-4 rounded-lg bg-amber-600 text-white
                                           font-semibold text-sm disabled:opacity-40">
                                Libertar adiantamento
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Aditivo aprovado depois de o escrow estar pago: a obra
                        cresceu e o dinheiro em caixa não. A libertação das
                        etapas fica travada até isto ser confirmado, por isso o
                        aviso tem de estar onde a operação olha. */}
                    {Number(detalhe.contrato.escrow_por_liquidar_kz || 0) > 0 && (
                      <div className="mt-3 p-3 rounded-lg bg-amber-950/40 border border-amber-800">
                        <div className="text-amber-300 font-semibold text-sm">
                          {kz(detalhe.contrato.escrow_por_liquidar_kz)} por liquidar
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          Veio de uma adenda aprovada com a obra já paga. Enquanto este
                          valor não entrar, nenhuma etapa pode ser libertada — não há
                          dinheiro em caixa que a pague.
                        </p>
                        <input value={refReforco} onChange={(e) => setRefReforco(e.target.value)}
                          placeholder="Referência da transferência"
                          className="w-full mt-2 px-3 min-h-[44px] rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-sm" />
                        <button onClick={confirmarReforco} disabled={busy || !refReforco.trim()}
                          className="w-full mt-2 min-h-[44px] rounded-lg bg-amber-700 text-white font-semibold disabled:opacity-40">
                          Confirmar entrada do reforço
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Dinheiro por libertar ── */}
                {!!(etapas || []).length && (
                  <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-sm space-y-2">
                    <div className="text-slate-400 text-xs uppercase tracking-wide font-semibold">
                      Etapas e pagamentos
                    </div>
                    {etapas.map((e) => {
                      const d = e.decomposicao || {};
                      // Quem liberta vai fazer uma transferência num banco: o
                      // número de que precisa é o líquido, não o valor da
                      // etapa. O ecrã mostrava só "1 280 000 Kz" ao lado do
                      // botão, e os 1 186 816 que saem de facto tinham de ser
                      // calculados noutro sítio — ou adivinhados.
                      const mostrarConta = e.estado === 'APROVADA' || e.pagamento === 'LIBERTADO';
                      return (
                        <div key={e.etapa_id} className="py-1.5 border-b border-slate-800 last:border-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="flex-1 min-w-[140px] text-slate-300">
                              {e.ordem}. {e.titulo}
                              <span className="text-slate-500"> · {kz(e.valor_kz)}</span>
                            </span>
                            {e.pagamento === 'LIBERTADO' ? (
                              <span className="text-xs text-green-400 font-semibold">
                                Libertado {e.libertado_em ? `a ${dt(e.libertado_em)}` : ''}
                              </span>
                            ) : e.estado === 'APROVADA' ? (
                              <button onClick={() => libertarEtapa(e)} disabled={busy}
                                className="min-h-[44px] px-3 rounded-lg bg-green-600 text-white text-sm font-semibold disabled:opacity-50">
                                Transferir {kz(d.liquido)}
                              </button>
                            ) : (
                              <span className="text-xs text-slate-500">{e.estado.replace(/_/g, ' ').toLowerCase()}</span>
                            )}
                          </div>

                          {mostrarConta && d.liquido != null && (
                            <div className="mt-1 text-[11px] text-slate-500 tabular-nums leading-relaxed">
                              {kz(d.bruto)} − {kz(d.comissao)} comissão − {kz(d.ivaComissao)} IVA
                              {/* "garantia" e nao "retencao": este ecra tambem
                                  decide a retencao na fonte, que e outra coisa
                                  — imposto para o Estado. Esta e a caucao da
                                  obra, dinheiro do empreiteiro que lhe volta
                                  com a aceitacao. A mesma palavra nos dois
                                  sitios e um engano a espera de acontecer. */}
                              {' '}− {kz(d.retido)} garantia
                              {Number(d.amortizacao) > 0 && <> − {kz(d.amortizacao)} adiantamento</>}
                              {' = '}
                              <span className="text-slate-300 font-semibold">{kz(d.liquido)}</span>
                              {d.prevista ? ' a transferir' : ' transferidos'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <input value={refPag} onChange={(ev) => setRefPag(ev.target.value)}
                      placeholder="Referencia da transferencia (opcional)"
                      className="w-full min-h-[44px] px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200" />
                  </div>
                )}

                {/* ── Caderno do trabalho ── */}
                {podePublicar && (
                  <div className="space-y-2 pt-2 border-t border-slate-800">
                    <div className="flex items-center gap-2 text-slate-300 font-semibold text-sm">
                      <FileText className="w-4 h-4" />
                      {revisao ? 'Rever o caderno' : 'Caderno de encargos'}
                    </div>
                    <p className="text-xs text-slate-500">
                      É o que torna as propostas comparáveis — todos os concorrentes recebem esta mesma base.
                      {revisao
                        ? ' Publicar uma versão nova avisa quem já respondeu para rever a proposta.'
                        : ' Publicá-lo abre as propostas.'}
                    </p>
                    <textarea rows={6} value={caderno.resumo}
                      onChange={(e) => setCaderno((c) => ({ ...c, resumo: e.target.value }))}
                      placeholder="Área, preparação necessária, demãos, equipamento, condições de acesso…"
                      className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 outline-none focus:border-blue-500" />
                    <input type="number" min="1" value={caderno.prazo_dias}
                      onChange={(e) => setCaderno((c) => ({ ...c, prazo_dias: e.target.value }))}
                      placeholder="Prazo pretendido (dias)"
                      className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 outline-none focus:border-blue-500" />
                    {/* A estimativa não é preço nem compromisso: decide quem é
                        convidado. Sem ela a obra vale zero para efeitos de
                        convite, e entram concorrentes que não a aguentam. */}
                    <div>
                      <label htmlFor="valor-estimado" className="block text-xs text-slate-400 mb-1">
                        Valor estimado da obra (Kz)
                      </label>
                      <input id="valor-estimado" type="number" min="1" inputMode="numeric"
                        value={caderno.valor_estimado_kz}
                        onChange={(e) => setCaderno((c) => ({ ...c, valor_estimado_kz: e.target.value }))}
                        placeholder="Ex.: 12000000"
                        className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 tabular-nums outline-none focus:border-blue-500" />
                      <p className="text-[11px] text-slate-500 mt-1">
                        Não vai para o cliente nem é compromisso. Decide quem pode concorrer:
                        acima do limiar, só empresas. Deixar em branco convida quem tiver a categoria,
                        seja qual for a dimensão.
                      </p>
                    </div>
                    {/* Rubricas: as linhas que cada concorrente vai orçamentar.
                        É o que permite ao cliente comparar "quadro eléctrico
                        com quadro eléctrico" em vez de comparar dois totais. */}
                    <div className="pt-3 border-t border-slate-800">
                      <div className="text-slate-400 text-xs uppercase tracking-wide font-semibold">
                        Rubricas a orçamentar
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1 mb-2">
                        Cada concorrente põe um preço em cada linha. Sem rubricas, as propostas
                        só se comparam pelo total — e dois totais iguais podem ser trabalhos
                        diferentes.
                      </p>

                      {rubricas.map((r, i) => (
                        <div key={i} className="flex gap-2 mb-2 flex-wrap">
                          <input value={r.descricao}
                            onChange={(e) => setRubricas((v) => v.map((x, j) => (j === i ? { ...x, descricao: e.target.value } : x)))}
                            placeholder="Ex.: Quadro eléctrico novo com diferencial"
                            className="flex-1 min-w-[160px] px-3 min-h-[44px] rounded-lg bg-slate-800 border border-slate-700 text-slate-200" />
                          <input type="number" min="0" value={r.quantidade}
                            onChange={(e) => setRubricas((v) => v.map((x, j) => (j === i ? { ...x, quantidade: e.target.value } : x)))}
                            placeholder="Qt."
                            className="w-20 px-2 min-h-[44px] rounded-lg bg-slate-800 border border-slate-700 text-slate-200 tabular-nums" />
                          <input value={r.unidade}
                            onChange={(e) => setRubricas((v) => v.map((x, j) => (j === i ? { ...x, unidade: e.target.value } : x)))}
                            placeholder="un."
                            className="w-20 px-2 min-h-[44px] rounded-lg bg-slate-800 border border-slate-700 text-slate-200" />
                          <button type="button" onClick={() => setRubricas((v) => v.filter((_, j) => j !== i))}
                            aria-label={`Retirar rubrica ${i + 1}`}
                            className="px-3 min-h-[44px] rounded-lg bg-slate-800 text-slate-400 border border-slate-700">
                            Retirar
                          </button>
                        </div>
                      ))}

                      <button type="button"
                        onClick={() => setRubricas((v) => [...v, { descricao: '', quantidade: '', unidade: '' }])}
                        className="w-full min-h-[44px] rounded-lg border border-dashed border-slate-700 text-slate-400 text-sm">
                        + Acrescentar rubrica
                      </button>
                    </div>

                    <button onClick={publicarCaderno} disabled={busy || !caderno.resumo.trim()}
                      className="w-full min-h-[44px] rounded-lg bg-blue-600 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40">
                      <Send className="w-4 h-4" />
                      {revisao ? 'Publicar revisão do caderno' : 'Publicar e abrir propostas'}
                    </button>
                  </div>
                )}

                {emConcurso && (
                  <div className="pt-2 border-t border-slate-800 space-y-2">
                    <div className="text-slate-300 font-semibold text-sm">Fila de convites</div>
                    {!fila ? (
                      <p className="text-slate-500 text-sm">A carregar…</p>
                    ) : (
                      <>
                        <p className="text-slate-400 text-sm">
                          Ronda {fila.ronda} · {fila.convites?.length || 0} de {fila.alvo_convites} convites
                        </p>
                        {(fila.convites?.length || 0) === 0 ? (
                          /* O caso que ficava invisivel: concurso aberto, zero
                             convidados. Sem esta linha o painel dizia "aberto a
                             propostas" e ninguem percebia porque nao chegava
                             nenhuma. */
                          <p className="text-amber-400 text-sm">
                            Ninguem foi convidado — nao havia prestador verificado nesta
                            categoria e valor quando o concurso abriu. Verifique capacidades
                            e reponha a fila.
                          </p>
                        ) : (
                          <ul className="space-y-1.5 text-sm">
                            {fila.convites.map((c) => (
                              <li key={c.convite_id} className="flex gap-3 text-slate-400">
                                <span className="flex-1">{c.full_name}</span>
                                <span className="text-xs">{c.concorrente === 'EMPRESA' ? 'Empresa' : 'Individual'}</span>
                                <span className="text-slate-200 text-xs">
                                  {fila.rotulos_convite?.[c.estado] || c.estado}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={reporConvites} disabled={busy}
                            className="min-h-[40px] px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-40">
                            Repor convites
                          </button>
                          <button onClick={novaRonda} disabled={busy}
                            className="min-h-[40px] px-4 rounded-lg border border-slate-700 text-slate-300 text-sm disabled:opacity-40">
                            Nova ronda
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* O perfil fiscal vem antes da facturação de propósito: é a
                    ordem em que as coisas acontecem, e ver o campo do NIF logo
                    acima explica sozinho porque é que a factura está bloqueada. */}
                {ambito && (
                  <div className="fixed inset-0 z-[60] bg-black/70 p-4 overflow-y-auto"
                    role="dialog" aria-modal="true" aria-label="Corrigir âmbito">
                    <div className="mx-auto w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5 my-8 space-y-3">
                      <h3 className="text-white font-bold">Âmbito: {ambito.rotulo.toLowerCase()}</h3>
                      <p className="text-sm text-slate-400">
                        Muda as perguntas que o avaliador leva para o local. Só se pode
                        corrigir enquanto a vistoria não estiver entregue.
                      </p>
                      <textarea rows={2} value={ambito.motivo}
                        onChange={(e) => setAmbito((a) => ({ ...a, motivo: e.target.value }))}
                        placeholder="Ex.: a descrição diz edifício em tosco, sem rede instalada."
                        className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm" />
                      <div className="flex gap-2">
                        <button onClick={() => setAmbito(null)}
                          className="flex-1 min-h-[44px] rounded-lg bg-slate-800 text-slate-300">Voltar</button>
                        <button onClick={gravarAmbito} disabled={busy || ambito.motivo.trim().length < 5}
                          className="flex-1 min-h-[44px] rounded-lg bg-cyan-600 text-white font-semibold disabled:opacity-40">
                          Corrigir
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {zona && (
                  <div className="fixed inset-0 z-[60] bg-black/70 p-4 overflow-y-auto"
                    role="dialog" aria-modal="true" aria-label="Corrigir zona">
                    <div className="mx-auto w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5 my-8 space-y-3">
                      <h3 className="text-white font-bold">Zona: {zona.rotulo}</h3>
                      <p className="text-sm text-slate-400">
                        É o que o prestador lê no lugar da morada enquanto a obra não
                        estiver paga, e decide a quem se oferece a visita. Uma zona
                        errada faz quem orça calcular a deslocação errada.
                      </p>
                      <textarea rows={2} value={zona.motivo}
                        onChange={(e) => setZona((z) => ({ ...z, motivo: e.target.value }))}
                        placeholder="Ex.: a morada diz Talatona e a zona estava Maianga."
                        className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm" />
                      <div className="flex gap-2">
                        <button onClick={() => setZona(null)}
                          className="flex-1 min-h-[44px] rounded-lg bg-slate-800 text-slate-300">Voltar</button>
                        <button onClick={gravarZona} disabled={busy || zona.motivo.trim().length < 5}
                          className="flex-1 min-h-[44px] rounded-lg bg-cyan-600 text-white font-semibold disabled:opacity-40">
                          Corrigir
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <PerfilFiscalObra projectId={detalhe.projecto.project_id}
                  aoGravar={() => setVersaoFiscal((v) => v + 1)} />
                <FaturacaoObra projectId={detalhe.projecto.project_id} versao={versaoFiscal} />

                {detalhe.propostas?.length > 0 && (
                  <div className="pt-2 border-t border-slate-800">
                    <div className="text-slate-300 font-semibold text-sm mb-2">
                      Propostas ({detalhe.propostas.length})
                    </div>
                    <ul className="space-y-1.5 text-sm">
                      {detalhe.propostas.map((pr) => (
                        <li key={pr.proposal_id} className="flex gap-3 text-slate-400">
                          <span className="flex-1">{pr.prestador}</span>
                          <span className="text-slate-200 font-semibold tabular-nums">{kz(pr.total_kz)}</span>
                          <span className="text-xs">{pr.duracao_dias ? `${pr.duracao_dias}d` : '—'}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsTriage;
